import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { signOut } from '../store/slices/authSlice'
import { colors } from '../constants/colors'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import TopQueriesChart from '../components/(admin)/charts/TopQueriesChart'
import ZeroHitTrendsChart from '../components/(admin)/charts/ZeroHitTrendsChart'
import BugReportsPieChart from '../components/(admin)/charts/BugReportsPieChart'
import { 
  fetchTopQueries,
  fetchZeroHitTrends,
  fetchBugReportsByStatus,
  fetchDashboardStats
} from '../store/slices/chartSlice'

const AI_HEALTH_LABELS = {
  healthy: 'Healthy',
  needs_attention: 'Needs Attention',
}

const Dashboard = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { business } = useSelector((state) => state.auth)

  const [isSigningOut, setIsSigningOut] = useState(false)

  // Get chart data from Redux
  const { 
    topQueries, 
    zeroHitTrends, 
    bugReportsByStatus: bugReports, 
    dashboardStats, 
    loading: isLoading 
  } = useSelector((state) => state.chart)

  // Fetch dashboard data for logged-in tenant only
  const tenantId = business?.id

  useEffect(() => {
    if (!tenantId) return
    dispatch(fetchTopQueries({ limit: 10, timeRange: '30', tenant: tenantId }))
    dispatch(fetchZeroHitTrends({ days: 30, tenant: tenantId }))
    dispatch(fetchBugReportsByStatus({ timeRange: '30', tenant: tenantId }))
    dispatch(fetchDashboardStats({ timeRange: '30', tenant: tenantId }))
  }, [dispatch, tenantId])

  const handleSignOut = async () => {
    setIsSigningOut(true)
    
    try {
      await dispatch(signOut()).unwrap()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      // Add a small delay to show the loader
      setTimeout(() => {
        navigate('/signin')
        setIsSigningOut(false)
      }, 500)
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    backgroundColor: colors.background.secondary,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    display: 'flex'
  }

  const mainContentStyle = {
    flex: 1,
    marginLeft: '280px',
    padding: '24px',
    backgroundColor: colors.background.secondary
  }

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px'
  }

  const headerLeftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerRightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  }

  const headerIconStyle = {
    width: '24px',
    height: '24px',
    cursor: 'pointer',
    color: colors.text.secondary
  }

  const notificationIconStyle = {
    ...headerIconStyle,
    position: 'relative'
  }

  const notificationDotStyle = {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    width: '8px',
    height: '8px',
    backgroundColor: '#ef4444',
    borderRadius: '50%'
  }

  const uploadButtonStyle = {
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: '500',
    color: colors.background.primary,
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease'
  }

  const titleStyle = {
    fontSize: '28px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: 0,
    marginBottom: '24px'
  }

  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '20px',
    marginBottom: '32px'
  }

  const statCardStyle = {
    backgroundColor: colors.background.primary,
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    textAlign: 'center'
  }

  const statTitleStyle = {
    fontSize: '14px',
    fontWeight: '500',
    color: colors.text.secondary,
    margin: '0 0 8px 0'
  }

  const statValueStyle = {
    fontSize: '32px',
    fontWeight: '700',
    color: colors.text.primary,
    margin: '0 0 4px 0'
  }

  const statSubtitleStyle = {
    fontSize: '12px',
    color: colors.text.secondary,
    margin: 0
  }

  const healthStatusStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.status.success,
    margin: 0
  }

  const chartsGridStyle = {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr',
    gap: '24px',
    marginBottom: '24px'
  }

  const chartCardStyle = {
    backgroundColor: colors.background.primary,
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
  }

  const chartTitleStyle = {
    fontSize: '18px',
    fontWeight: '600',
    color: colors.text.primary,
    margin: '0 0 20px 0'
  }

  const chartImageStyle = {
    width: '100%',
    height: 'auto',
    borderRadius: '4px'
  }

  const legendStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginTop: '16px'
  }

  const legendItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  }

  const legendDotStyle = {
    width: '12px',
    height: '12px',
    borderRadius: '50%'
  }

  const notificationsCardStyle = {
    backgroundColor: colors.background.primary,
    padding: '24px',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    minHeight: '200px'
  }

  const loaderOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999
  }

  const loaderContainerStyle = {
    backgroundColor: colors.background.primary,
    padding: '40px',
    borderRadius: '12px',
    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
    textAlign: 'center',
    minWidth: '200px'
  }

  const spinnerStyle = {
    width: '40px',
    height: '40px',
    border: '4px solid #e2e8f0',
    borderTop: `4px solid ${colors.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px auto'
  }

  const loaderTextStyle = {
    fontSize: '16px',
    color: colors.text.primary,
    margin: 0,
    fontWeight: '500'
  }

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Header */}
        <PageTopBar
          title="Business Dashboard"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
          onSignOut={handleSignOut}
        />

        {/* Stats Cards */}
        <div style={statsGridStyle}>
          <div style={statCardStyle}>
            <h3 style={statTitleStyle}>Total Queries</h3>
            <h2 style={statValueStyle}>
              {isLoading ? '...' : dashboardStats.totalQueries.toLocaleString()}
            </h2>
            <p style={statSubtitleStyle}>last 30 days</p>
          </div>
          <div style={statCardStyle}>
            <h3 style={statTitleStyle}>Bug Reports</h3>
            <h2 style={statValueStyle}>
              {isLoading ? '...' : dashboardStats.totalBugReports}
            </h2>
            <p style={statSubtitleStyle}>
              {dashboardStats.openBugReports} open
            </p>
          </div>
          <div style={statCardStyle}>
            <h3 style={statTitleStyle}>AI Health</h3>
            <p style={{
              ...healthStatusStyle,
              color: dashboardStats.aiHealth === 'healthy' 
                ? colors.status.success 
                : dashboardStats.aiHealth === 'needs_attention'
                ? colors.status.warning
                : colors.status.error
            }}>
              {isLoading ? '...' : (AI_HEALTH_LABELS[dashboardStats.aiHealth] ?? dashboardStats.aiHealth)}
            </p>
            <p style={statSubtitleStyle}>
              {dashboardStats.successRate}% success rate
            </p>
          </div>
        </div>

        {/* Charts Grid */}
        <div style={chartsGridStyle}>
          {/* Top Queries Chart */}
          <div style={chartCardStyle}>
            <h3 style={chartTitleStyle}>Top Queries</h3>
            <TopQueriesChart data={topQueries} isLoading={isLoading} />
          </div>

          {/* Bug Reports Chart */}
          <div style={chartCardStyle}>
            <h3 style={chartTitleStyle}>Bug Reports by Status</h3>
            <BugReportsPieChart data={bugReports} isLoading={isLoading} />
          </div>
        </div>

        {/* Zero Hit Trends Chart - Full Width */}
        <div style={{...chartCardStyle, marginBottom: '24px'}}>
          <h3 style={chartTitleStyle}>Zero Hit Trends (Last 30 Days)</h3>
          <ZeroHitTrendsChart data={zeroHitTrends} isLoading={isLoading} />
        </div>

        {/* Notifications */}
        <div style={notificationsCardStyle}>
          <h3 style={chartTitleStyle}>Notifications</h3>
          {/* Empty notifications area */}
        </div>
      </div>

      {/* Sign Out Loader Overlay */}
      {isSigningOut && (
        <div style={loaderOverlayStyle}>
          <div style={loaderContainerStyle}>
            <div style={spinnerStyle}></div>
            <p style={loaderTextStyle}>Signing out...</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard